import React from "react";
import { useToast } from "../Toast/ToastContext";

// Composite error boundary that combines multiple error handling strategies
class CompositeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      errorTime: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorId: Math.random().toString(36).substr(2, 9),
      errorTime: new Date(),
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("CompositeErrorBoundary caught an error:", error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // Enhanced error logging for production monitoring
    const errorDetails = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.props.userId,
      sessionId: this.props.sessionId,
      buildVersion: process.env.REACT_APP_VERSION || "unknown",
    };

    // Log to external monitoring service
    if (window.gtag) {
      window.gtag("event", "exception", {
        description: error.toString(),
        fatal: false,
        custom_map: errorDetails,
      });
    }

    // Send to custom error tracking endpoint if available
    if (this.props.onErrorReport) {
      this.props.onErrorReport(errorDetails);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      errorTime: null,
    });
  };

  handleEmergencyMode = () => {
    // Enable emergency mode - minimal functionality
    if (this.props.onEmergencyMode) {
      this.props.onEmergencyMode();
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const { level = "application", componentName = "Application" } =
        this.props;

      return (
        <div className="composite-error-boundary">
          <div className="container-fluid py-4">
            <div className="row justify-content-center">
              <div className="col-md-8 col-lg-6">
                <div className="card border-danger">
                  <div className="card-header bg-danger text-white">
                    <h4 className="card-title mb-0">
                      <i className="fas fa-exclamation-circle me-2"></i>
                      {level === "critical"
                        ? "Critical System Error"
                        : "Application Error"}
                    </h4>
                  </div>
                  <div className="card-body">
                    <p className="card-text">
                      <strong>{componentName}</strong> has encountered an
                      unexpected error and needs to restart.
                    </p>

                    {level === "critical" && (
                      <div className="alert alert-danger">
                        <h6 className="alert-heading">
                          <i className="fas fa-exclamation-triangle me-2"></i>
                          Critical Trading System Error
                        </h6>
                        <p className="mb-0">
                          A critical error has occurred that may affect trading
                          operations. Please contact support immediately if you
                          have open positions.
                        </p>
                      </div>
                    )}

                    {/* Error ID for support */}
                    <div className="alert alert-info">
                      <h6>Error Reference</h6>
                      <p className="mb-1">
                        <strong>Error ID:</strong>{" "}
                        <code>{this.state.errorId}</code>
                      </p>
                      <p className="mb-0">
                        <strong>Time:</strong>{" "}
                        {this.state.errorTime &&
                          this.state.errorTime.toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                          })}
                      </p>
                    </div>

                    {/* Recovery options */}
                    <div className="d-grid gap-2 d-md-flex justify-content-md-start">
                      <button
                        className="btn btn-primary"
                        onClick={this.handleRetry}
                      >
                        <i className="fas fa-redo me-2"></i>
                        Try Again
                      </button>

                      <button
                        className="btn btn-warning"
                        onClick={this.handleEmergencyMode}
                      >
                        <i className="fas fa-shield-alt me-2"></i>
                        Emergency Mode
                      </button>

                      <button
                        className="btn btn-secondary"
                        onClick={() => window.location.reload()}
                      >
                        <i className="fas fa-sync me-2"></i>
                        Reload App
                      </button>
                    </div>

                    {/* Emergency contact information */}
                    {level === "critical" && (
                      <div className="mt-4">
                        <h6>Emergency Support</h6>
                        <div className="row g-2">
                          <div className="col-sm-6">
                            <a
                              href="mailto:support@gohustle.com?subject=Critical%20Error%20-%20ID:%20{this.state.errorId}"
                              className="btn btn-outline-danger btn-sm w-100"
                            >
                              <i className="fas fa-envelope me-1"></i>
                              Email Support
                            </a>
                          </div>
                          <div className="col-sm-6">
                            <button
                              className="btn btn-outline-info btn-sm w-100"
                              onClick={() =>
                                window.open(
                                  "https://kite.zerodha.com",
                                  "_blank",
                                )
                              }
                            >
                              <i className="fas fa-external-link-alt me-1"></i>
                              Kite Backup
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Technical details for developers */}
                    {process.env.NODE_ENV === "development" && (
                      <details className="mt-4">
                        <summary className="btn btn-outline-secondary btn-sm">
                          <i className="fas fa-code me-1"></i>
                          Developer Details
                        </summary>
                        <div className="mt-2">
                          <pre
                            className="bg-light p-2 border rounded"
                            style={{ fontSize: "12px", overflow: "auto" }}
                          >
                            <strong>Error:</strong>{" "}
                            {this.state.error && this.state.error.toString()}
                            {this.state.error && this.state.error.stack}
                            {this.state.errorInfo &&
                              this.state.errorInfo.componentStack}
                          </pre>
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC for easy wrapping with toast integration
export const withCompositeErrorBoundary = (WrappedComponent, options = {}) => {
  return function CompositeErrorBoundaryWrapper(props) {
    const { showError } = useToast();

    const handleErrorReport = async (errorDetails) => {
      try {
        // Send error to backend if endpoint exists
        const response = await fetch("/api/errors", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(errorDetails),
        });

        if (!response.ok) {
          console.warn("Failed to send error report to backend");
        }
      } catch (error) {
        console.warn("Error reporting failed:", error);
      }

      // Show user-friendly error toast
      showError("An unexpected error occurred. Support has been notified.");
    };

    return (
      <CompositeErrorBoundary
        level={options.level || "application"}
        componentName={
          options.componentName ||
          WrappedComponent.displayName ||
          WrappedComponent.name
        }
        onErrorReport={handleErrorReport}
        onEmergencyMode={options.onEmergencyMode}
        userId={props.userId}
        sessionId={props.sessionId}
        {...options}
      >
        <WrappedComponent {...props} />
      </CompositeErrorBoundary>
    );
  };
};

export default CompositeErrorBoundary;
